import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchFormSubmissions } from './dummyData';
import { FormSubmission, EvidenceItem } from './types';
import styles from './form-submissions.module.css';
import { ChevronRight, Users, Loader } from 'lucide-react';

export function FormSubmissionList() {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSubmissions = async () => {
      setLoading(true);
      const data = await fetchFormSubmissions();
      setSubmissions(data);
      setLoading(false);
    };
    loadSubmissions();
  }, []);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getEvidenceCount = (submission: FormSubmission): number => {
    return Object.values(submission.evidence).reduce(
      (sum: number, items: EvidenceItem[]) => sum + items.length,
      0
    );
  };

  const getQualificationConfig = (qual: FormSubmission['qualification']) => {
    switch (qual) {
      case 'yes':
        return {
          label: 'Qualifies',
          className: styles.qualificationYes,
        };
      case 'no':
        return {
          label: 'Unqualified',
          className: styles.qualificationNo,
        };
      case 'maybe':
        return {
          label: 'Needs Review',
          className: styles.qualificationMaybe,
        };
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader size={40} style={{ animation: 'spin 1s linear infinite', color: '#fa5d19' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageWrapper}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <Users size={22} className={styles.textOrange} />
            Form Submissions
          </h1>
          <p className={styles.pageSubtitle}>Review O-1A visa application submissions</p>
        </div>

        {/* Stats Summary */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{submissions.length}</div>
            <div className={styles.statLabel}>Total</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {submissions.filter(s => s.status === 'in_review').length}
            </div>
            <div className={styles.statLabel}>In Review</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {submissions.filter(s => s.status === 'needs_info').length}
            </div>
            <div className={styles.statLabel}>Need Attention</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {submissions.filter(s => s.status === 'approved').length}
            </div>
            <div className={styles.statLabel}>Approved</div>
          </div>
        </div>

        {/* Submissions List */}
        <div className={styles.submissionsList}>
          {submissions.map(submission => {
            const qualConfig = getQualificationConfig(submission.qualification);

            return (
              <Link
                key={submission.id}
                to={`/forms/${submission.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div className={styles.submissionCard}>
                  <div className={styles.submissionAvatar}>
                    {getInitials(submission.contact.fullName)}
                  </div>

                  <div className={styles.submissionInfo}>
                    <div className={styles.submissionName}>{submission.contact.fullName}</div>
                    <div className={styles.submissionMeta}>
                      {submission.professional.profession} · {getEvidenceCount(submission)} evidence
                      items
                    </div>
                  </div>

                  <div className={styles.submissionBadges}>
                    <span className={`${styles.qualificationBadge} ${qualConfig.className}`}>
                      {qualConfig.label}
                    </span>
                    <span className={`${styles.badge} ${styles.badgeVisa}`}>
                      {submission.visaType}
                    </span>
                    <span className={styles.submissionDate}>
                      {formatDate(submission.submittedAt)}
                    </span>
                  </div>

                  <ChevronRight size={18} className={styles.submissionArrow} />
                </div>
              </Link>
            );
          })}
        </div>

        {submissions.length === 0 && (
          <div className={styles.emptyState}>
            <p className={styles.emptyStateText}>No data available</p>
          </div>
        )}
      </div>
    </div>
  );
}
